/**
 * Projects kept in a real folder on the user's computer, through the File
 * System Access API (Chrome and Edge). The folder's handle is stored with the
 * project's record, so the browser remembers it; the browser asks the user
 * again for permission after a restart. Other browsers import a copy into
 * IndexedDB instead.
 */
import { isScriptSource, normalizeProjectPath } from '@animo-editor/format';
import type { Storage } from '@animo-editor/project';
import { readScriptFields, type SourceFields } from './script-fields.js';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Names skipped everywhere, as on disk: hidden files and folders, and node_modules. */
export const skipName = (name: string) => name.startsWith('.') || name === 'node_modules';

/** Whether this browser can edit a folder in place. */
export function canOpenFolders(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

/** Asks the user for a folder to edit. Undefined if they cancel. */
export async function pickFolder(): Promise<FileSystemDirectoryHandle | undefined> {
  try {
    return await (window as unknown as { showDirectoryPicker(o: object): Promise<FileSystemDirectoryHandle> }).showDirectoryPicker({ id: 'animo-project', mode: 'readwrite' });
  } catch (e) {
    if ((e as DOMException).name === 'AbortError') return undefined;
    throw e;
  }
}

type PermissionHandle = FileSystemHandle & {
  queryPermission?(o: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission?(o: { mode: 'readwrite' }): Promise<PermissionState>;
};

/** Whether the page may read and write the folder now, without asking. */
export async function folderAccess(dir: FileSystemDirectoryHandle): Promise<PermissionState> {
  const h = dir as PermissionHandle;
  return h.queryPermission ? h.queryPermission({ mode: 'readwrite' }) : 'granted';
}

/** Asks for read and write access to the folder. Only works from a click. */
export async function requestFolderAccess(dir: FileSystemDirectoryHandle): Promise<boolean> {
  const h = dir as PermissionHandle;
  if ((await folderAccess(dir)) === 'granted') return true;
  return (await h.requestPermission?.({ mode: 'readwrite' })) === 'granted';
}

const notFound = (e: unknown) => {
  const name = (e as DOMException)?.name;
  return name === 'NotFoundError' || name === 'TypeMismatchError';
};

/** The parts of a project path, checked to stay inside the folder. */
function partsOf(projectPath: string): string[] {
  return normalizeProjectPath(projectPath).split('/');
}

/** A folder on the user's computer, as the project model's Storage. */
export class FolderStorage implements Storage {
  readonly root: string;

  /** `prefix` is '' for the folder itself, or a subfolder with a trailing slash, such as 'public/'. */
  constructor(
    readonly dir: FileSystemDirectoryHandle,
    private prefix = '',
    private touched: () => void = () => {},
  ) {
    this.root = `folder:${dir.name}/${prefix}`;
  }

  /** A path's parts from the folder, checked to stay inside the prefix too. */
  private parts(path: string): string[] {
    return [...this.prefix.split('/').filter(Boolean), ...partsOf(path)];
  }

  /** The folder holding a path, made if `create`; undefined if it doesn't exist. */
  private async folderOf(parts: string[], create = false): Promise<FileSystemDirectoryHandle | undefined> {
    let dir = this.dir;
    for (const name of parts.slice(0, -1)) {
      try {
        dir = await dir.getDirectoryHandle(name, { create });
      } catch (e) {
        if (notFound(e)) return undefined;
        throw e;
      }
    }
    return dir;
  }

  private async file(path: string): Promise<FileSystemFileHandle | undefined> {
    const parts = this.parts(path);
    const dir = await this.folderOf(parts);
    if (!dir) return undefined;
    try {
      return await dir.getFileHandle(parts.at(-1)!);
    } catch (e) {
      if (notFound(e)) return undefined;
      throw e;
    }
  }

  /** The bytes of a file, or undefined if there is none. */
  async bytes(path: string): Promise<Uint8Array | undefined> {
    const handle = await this.file(path);
    if (!handle) return undefined;
    return new Uint8Array(await (await handle.getFile()).arrayBuffer());
  }

  async read(path: string): Promise<string | undefined> {
    const bytes = await this.bytes(path);
    return bytes && decoder.decode(bytes);
  }

  private async put(path: string, data: Uint8Array): Promise<void> {
    const parts = this.parts(path);
    const dir = (await this.folderOf(parts, true))!;
    const handle = await dir.getFileHandle(parts.at(-1)!, { create: true });
    // The browser writes to a swap file and moves it over the target on close, so readers never see half a file.
    const out = await handle.createWritable();
    try {
      await out.write(data as FileSystemWriteChunkType);
      await out.close();
    } catch (e) {
      await out.abort().catch(() => {});
      throw e;
    }
  }

  async write(path: string, text: string): Promise<void> {
    await this.put(path, encoder.encode(text));
    this.touched();
  }

  readBytes(path: string): Promise<Uint8Array | undefined> {
    return this.bytes(path);
  }

  async replace(path: string, bytes: Uint8Array): Promise<void> {
    await this.put(path, bytes);
    this.touched();
  }

  async stat(path: string): Promise<{ size: number; mtime: number } | undefined> {
    const file = await (await this.file(path))?.getFile();
    return file && { size: file.size, mtime: file.lastModified };
  }

  async create(path: string, bytes: Uint8Array): Promise<void> {
    if (await this.exists(path)) throw new Error(`${path} already exists`);
    await this.put(path, bytes);
    this.touched();
  }

  async exists(path: string): Promise<boolean> {
    return (await this.file(path)) !== undefined;
  }

  async remove(path: string): Promise<void> {
    const parts = this.parts(path);
    const dir = await this.folderOf(parts);
    try {
      await dir?.removeEntry(parts.at(-1)!);
    } catch (e) {
      if (!notFound(e)) throw e;
    }
    this.touched();
  }

  async move(from: string, to: string): Promise<void> {
    const bytes = await this.bytes(from);
    if (bytes === undefined) throw new Error(`${from} does not exist`);
    if (await this.exists(to)) throw new Error(`${to} already exists`);
    // Copy, then remove, so a failure part way never loses the file.
    await this.put(to, bytes);
    await this.remove(from);
  }

  async list(filter: (path: string) => boolean): Promise<string[]> {
    const start = this.prefix ? await this.folderOf(this.parts('x')) : this.dir;
    if (!start) return [];
    const out: string[] = [];
    for (const { path } of await walk(start)) if (filter(path)) out.push(path);
    return out.sort();
  }
}

export interface FolderEntry {
  path: string;
  handle: FileSystemFileHandle;
}

/** Every file under a folder, skipping hidden files and folders and node_modules. */
export async function walk(dir: FileSystemDirectoryHandle, prefix = ''): Promise<FolderEntry[]> {
  const out: FolderEntry[] = [];
  for await (const [name, handle] of (dir as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> }).entries()) {
    if (skipName(name)) continue;
    const path = prefix + name;
    if (handle.kind === 'directory') out.push(...(await walk(handle as FileSystemDirectoryHandle, `${path}/`)));
    else out.push({ path, handle: handle as FileSystemFileHandle });
  }
  return out;
}

/** Every file of a folder project, for export and copying into the browser. */
export async function folderFiles(dir: FileSystemDirectoryHandle): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  for (const { path, handle } of await walk(dir)) out.set(path, new Uint8Array(await (await handle.getFile()).arrayBuffer()));
  return out;
}

/** Writes a project's files into an empty folder, for new folder projects. */
export async function writeFiles(dir: FileSystemDirectoryHandle, files: Map<string, Uint8Array>): Promise<void> {
  const storage = new FolderStorage(dir);
  for (const [path, bytes] of files) await storage.create(path, bytes);
}

/** Whether a folder has nothing in it but hidden files. */
export async function isEmptyFolder(dir: FileSystemDirectoryHandle): Promise<boolean> {
  for await (const name of (dir as unknown as { keys(): AsyncIterable<string> }).keys()) if (!skipName(name)) return false;
  return true;
}

type Snapshot = Map<string, string>;

async function snapshot(dir: FileSystemDirectoryHandle): Promise<Snapshot> {
  const out: Snapshot = new Map();
  for (const { path, handle } of await walk(dir)) {
    const file = await handle.getFile();
    out.set(path, `${file.lastModified}:${file.size}`);
  }
  return out;
}

/**
 * Watches a folder for changes made by anything (the editor's own saves
 * included; the project recognises those by content). Calls `changed` with
 * the paths that were added, changed or removed. Uses the browser's
 * FileSystemObserver where there is one, and otherwise compares the folder's
 * file dates every `pollMs` while the page is visible.
 */
export function watchFolder(dir: FileSystemDirectoryHandle, changed: (paths: string[]) => void, pollMs = 1000): { close(): void } {
  let closed = false;
  let last: Snapshot | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const poll = async () => {
    if (closed) return;
    try {
      if (typeof document === 'undefined' || document.visibilityState === 'visible' || !last) {
        const next = await snapshot(dir);
        if (last) {
          const paths: string[] = [];
          for (const [path, stamp] of next) if (last.get(path) !== stamp) paths.push(path);
          for (const path of last.keys()) if (!next.has(path)) paths.push(path);
          if (paths.length && !closed) changed(paths.sort());
        }
        last = next;
      }
    } catch {
      // The folder may be gone or access withdrawn; keep trying, as it may come back.
    }
    if (!closed) timer = setTimeout(() => void poll(), pollMs);
  };

  const Observer = (globalThis as unknown as { FileSystemObserver?: new (cb: (records: { relativePathComponents: string[]; relativePathMovedFrom?: string[]; type: string }[]) => void) => { observe(h: FileSystemHandle, o: object): Promise<void>; disconnect(): void } }).FileSystemObserver;
  if (Observer) {
    const observer = new Observer((records) => {
      const paths = new Set<string>();
      let rescan = false;
      for (const r of records) {
        if (r.type === 'errored' || r.type === 'unknown') rescan = true;
        for (const parts of [r.relativePathComponents, r.relativePathMovedFrom ?? []]) {
          if (parts.length && !parts.some(skipName)) paths.add(parts.join('/'));
        }
      }
      // When the browser lost track, compare the whole folder instead.
      if (rescan) void snapshot(dir).then((next) => {
        const all = new Set([...(last?.keys() ?? []), ...next.keys()]);
        last = next;
        if (!closed) changed([...all].sort());
      });
      else if (paths.size && !closed) changed([...paths].sort());
    });
    observer
      .observe(dir, { recursive: true })
      .then(() => void snapshot(dir).then((s) => (last = s)))
      .catch(() => {
        observer.disconnect();
        void poll();
      });
    return {
      close() {
        closed = true;
        observer.disconnect();
        clearTimeout(timer);
      },
    };
  }
  void poll();
  return {
    close() {
      closed = true;
      clearTimeout(timer);
    },
  };
}

/** A script file, as the editor's Scripts section lists it. */
export interface FolderScript {
  path: string;
  url: string;
  file: string;
  mtime: number;
  read: SourceFields | { error: string };
}

const SCRIPT_SOURCE = /\.(ts|mts|js|mjs)$/;
const NOT_SCRIPT_DIRS = new Set(['dist', 'build', 'coverage']);
const MAX_SCRIPT_BYTES = 512 * 1024;

/** True for source text that defines a script, as the dev server checks it. */
export { isScriptSource };

/** Whether a path in the folder could be a script: source outside the served root and build output. */
export function scriptCandidate(path: string, rootPrefix: string): boolean {
  if (!SCRIPT_SOURCE.test(path) || path.endsWith('.d.ts')) return false;
  if (rootPrefix && path.startsWith(rootPrefix)) return false;
  return !path.split('/').some((part) => NOT_SCRIPT_DIRS.has(part) || skipName(part));
}

/** A project's code files, for listing and creating scripts: a folder here, or a desktop project. Paths are from the folder holding project.json. */
export interface CodeFiles {
  /** The folder's name, for the file label scripts show. */
  name: string;
  list(): Promise<string[]>;
  bytes(path: string): Promise<Uint8Array | undefined>;
  /** When the file last changed, in milliseconds. */
  mtime(path: string): Promise<number>;
  /** Writes a new file; refuses to replace one. */
  create(path: string, bytes: Uint8Array): Promise<void>;
}

/** A folder picked in the browser, as CodeFiles. */
export function folderCode(dir: FileSystemDirectoryHandle): CodeFiles {
  const storage = new FolderStorage(dir);
  return {
    name: dir.name,
    list: async () => (await walk(dir)).map((e) => e.path),
    bytes: (path) => storage.bytes(path),
    mtime: async (path) => (await storage.stat(path))?.mtime ?? 0,
    create: (path, bytes) => storage.create(path, bytes),
  };
}

/**
 * The game's scripts in a folder project, for the editor's Scripts section,
 * as the dev server lists them. Their fields are read from the source text,
 * since the browser can't run the game's code.
 */
export class FolderScripts {
  constructor(
    private files: CodeFiles,
    /** The project's served root, such as 'public/', which holds no scripts. */
    private rootPrefix: string,
  ) {}

  private async listing(path: string): Promise<FolderScript | undefined> {
    const bytes = await this.files.bytes(path);
    if (!bytes || bytes.length > MAX_SCRIPT_BYTES) return undefined;
    const text = decoder.decode(bytes);
    if (!isScriptSource(text)) return undefined;
    return { path, url: '', file: `${this.files.name}/${path}`, mtime: await this.files.mtime(path), read: readScriptFields(text) };
  }

  async list(): Promise<FolderScript[]> {
    const out: FolderScript[] = [];
    for (const path of await this.files.list()) {
      if (!scriptCandidate(path, this.rootPrefix)) continue;
      const listing = await this.listing(path).catch(() => undefined);
      if (listing) out.push(listing);
    }
    return out.sort((a, b) => a.path.localeCompare(b.path));
  }

  /** Writes a new script. Refuses paths in the served root, in build output, or that exist. */
  async create(path: string, text: string): Promise<FolderScript> {
    const rel = normalizeProjectPath(path);
    if (!SCRIPT_SOURCE.test(rel) || rel.endsWith('.d.ts')) throw new Error('A script file ends in .ts or .js');
    if (this.rootPrefix && rel.startsWith(this.rootPrefix)) throw new Error(`${rel} is in the folder the game serves; put scripts with the game's code, such as src/scripts`);
    if (!scriptCandidate(rel, this.rootPrefix)) throw new Error(`Scripts can't go in ${rel}`);
    await this.files.create(rel, encoder.encode(text));
    const listing = await this.listing(rel);
    if (!listing) throw new Error(`${rel} was written, but it doesn't define a Script class`);
    return listing;
  }
}
